import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Search, Wrench, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/* ------------------------------------------------------------------ */
/* Single Operation Row                                                */
/* ------------------------------------------------------------------ */
const OperationRow = ({ op, idx, bomId, revisionId, lineId, onRefresh, isDraft }) => {
  const [qty, setQty] = useState(op.quantity || '');
  const [hrs, setHrs] = useState(op.hours || '');
  const [manualCost, setManualCost] = useState(op.manual_cost || '');
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const costModelDisplay = () => {
    switch (op.cost_model) {
      case 'per_piece': return `${op.rate?.toLocaleString() || 0}/piece`;
      case 'per_unit': return `${op.rate?.toLocaleString() || 0}/${op.unit_label || 'unit'} x ${op.quantity || 0}`;
      case 'hourly_rate': return `${op.rate?.toLocaleString() || 0}/hr x ${op.hours || 0}h`;
      case 'manual': return 'Manual entry';
      default: return '';
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revisionId}/lines/${lineId}/operations/${idx}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.ok) { toast.success('Operation removed'); onRefresh(); }
      else { const e = await res.json(); toast.error(e.detail || 'Remove failed'); }
    } catch { toast.error('Remove failed'); }
    setRemoving(false);
  };

  const handleUpdate = useCallback(async (field, value) => {
    setSaving(true);
    try {
      const tk = getToken();
      const body = { [field]: parseFloat(value) };
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revisionId}/lines/${lineId}/operations/${idx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) { onRefresh(); }
      else { const e = await res.json(); toast.error(e.detail || 'Update failed'); }
    } catch { toast.error('Update failed'); }
    setSaving(false);
  }, [bomId, revisionId, lineId, idx, onRefresh]);

  const debouncedUpdate = useCallback((field, value) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const v = parseFloat(value);
      if (!isNaN(v) && v > 0) handleUpdate(field, value);
    }, 600);
  }, [handleUpdate]);

  return (
    <div className="group border border-slate-700/40 rounded-md p-2 bg-slate-800/30 hover:bg-slate-800/50 transition-colors" data-testid={`fab-op-row-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500 font-mono">{idx + 1}</span>
            <span className="text-[11px] text-slate-200 truncate font-medium">{op.operation_name || op.operation_code}</span>
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5 ml-4">
            {costModelDisplay()}
          </div>
        </div>
        <span className="font-mono text-[11px] text-emerald-400 shrink-0 font-semibold" data-testid={`fab-op-cost-${idx}`}>
          {(op.operation_cost || op.computed_cost || op.manual_cost || 0).toLocaleString()}
        </span>
        {isDraft && (
          <button onClick={handleRemove} disabled={removing}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500/60 hover:text-red-400 p-0.5"
            data-testid={`fab-op-remove-${idx}`}>
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Editable fields for variable cost models */}
      {isDraft && (
        <div className="ml-4 mt-1 flex items-center gap-2">
          {op.cost_model === 'per_unit' && (
            <>
              <Input type="number" value={qty} onChange={e => { setQty(e.target.value); debouncedUpdate('quantity', e.target.value); }}
                className="h-5 w-16 text-[10px] bg-slate-800 border-slate-600 text-white font-mono p-1" min="1" step="1"
                data-testid={`fab-op-qty-${idx}`} />
              <span className="text-[9px] text-slate-600">{op.unit_label || 'units'}</span>
            </>
          )}
          {op.cost_model === 'hourly_rate' && (
            <>
              <Input type="number" value={hrs} onChange={e => { setHrs(e.target.value); debouncedUpdate('hours', e.target.value); }}
                className="h-5 w-16 text-[10px] bg-slate-800 border-slate-600 text-white font-mono p-1" min="0.01" step="0.25"
                data-testid={`fab-op-hrs-${idx}`} />
              <span className="text-[9px] text-slate-600">hours</span>
            </>
          )}
          {op.cost_model === 'manual' && (
            <>
              <Input type="number" value={manualCost} onChange={e => { setManualCost(e.target.value); debouncedUpdate('manual_cost', e.target.value); }}
                className="h-5 w-20 text-[10px] bg-slate-800 border-slate-600 text-white font-mono p-1" min="0"
                data-testid={`fab-op-manual-cost-${idx}`} />
              <span className="text-[9px] text-slate-600">total</span>
            </>
          )}
          {saving && <span className="text-[9px] text-blue-400 animate-pulse">...</span>}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Add Operation Popover — Powered by Process Library                  */
/* Source: /api/v1/process-library/processes?domain=fabrication         */
/* Apply: /api/v1/process-library/apply-to-line                        */
/* ------------------------------------------------------------------ */
const AddOperationPopover = ({ bomId, revisionId, lineId, onRefresh, onClose, onBusy }) => {
  const [processes, setProcesses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ operation_name: '', cost_model: 'per_piece', rate: '', quantity: '', hours: '', manual_cost: '', notes: '' });
  const [adding, setAdding] = useState(null);
  const [selected, setSelected] = useState(new Map()); // process_code → process object
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const tk = getToken();
      try {
        const res = await fetchWithAuth(
          `${API}/api/v1/process-library/processes?domain=fabrication&status=active&limit=200`,
          { headers: { Authorization: `Bearer ${tk}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setProcesses(data.items || []);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const filtered = processes.filter(p =>
    p.process_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.process_code?.toLowerCase().includes(search.toLowerCase()) ||
    p.process_category?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(p => {
    const cat = p.process_category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });

  const toggleSelect = (p) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(p.process_code)) next.delete(p.process_code);
      else next.set(p.process_code, p);
      return next;
    });
  };

  const applySelected = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    onBusy?.('applying');
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/process-library/batch-apply-processes-to-line`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          process_codes: [...selected.keys()],
          bom_id: bomId,
          revision_id: revisionId,
          line_id: lineId,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Applied ${result.applied} operation${result.applied !== 1 ? 's' : ''}`);
        onRefresh(); onClose();
      } else { const e = await res.json(); toast.error(e.detail || 'Apply failed'); }
    } catch { toast.error('Apply failed'); }
    setApplying(false);
    onBusy?.(null);
  };

  const addManual = async () => {
    if (!manualForm.operation_name.trim()) { toast.error('Operation name required'); return; }
    setAdding('manual');
    const tk = getToken();
    const body = {
      operation_name: manualForm.operation_name,
      cost_model: manualForm.cost_model,
      rate: ['per_piece', 'per_unit', 'hourly_rate'].includes(manualForm.cost_model) ? parseFloat(manualForm.rate) || 0 : undefined,
      quantity: manualForm.cost_model === 'per_unit' ? parseFloat(manualForm.quantity) || 1 : undefined,
      hours: manualForm.cost_model === 'hourly_rate' ? parseFloat(manualForm.hours) || 1 : undefined,
      manual_cost: manualForm.cost_model === 'manual' ? parseFloat(manualForm.manual_cost) || 0 : undefined,
      notes: manualForm.notes || undefined,
    };
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revisionId}/lines/${lineId}/operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) { toast.success(`Added: ${manualForm.operation_name}`); onRefresh(); onClose(); }
      else { const e = await res.json(); toast.error(e.detail || 'Add failed'); }
    } catch { toast.error('Add failed'); }
    setAdding(null);
  };

  const costDisplay = (p) => {
    const cm = p.cost_model || {};
    const rate = cm.rate || 0;
    const label = cm.unit_label || 'piece';
    if (rate <= 0) return '';
    return `${rate}/${label}`;
  };

  return (
    <div className="border border-blue-600/40 rounded-lg bg-slate-900 p-3 mt-2 shadow-xl" data-testid="fab-add-op-popover">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider">Add Operation</span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
      </div>

      {!showManual ? (
        <>
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
            <Input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search operations..."
              className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white pl-7"
              data-testid="fab-search-operations" />
          </div>

          <div className="max-h-[200px] overflow-y-auto space-y-1.5">
            {loading ? <div className="text-[10px] text-slate-500 text-center py-4">Loading from Process Library...</div> : (
              Object.keys(grouped).length === 0 ? (
                <div className="text-[10px] text-slate-500 text-center py-3">No matching operations</div>
              ) : (
                Object.entries(grouped).map(([cat, items]) => (
                  <div key={cat}>
                    <div className="text-[9px] text-slate-500 font-semibold uppercase tracking-wider px-1 py-0.5 sticky top-0 bg-slate-900">{cat}</div>
                    {items.map(p => {
                      const isChecked = selected.has(p.process_code);
                      return (
                        <button key={p.process_code} onClick={() => toggleSelect(p)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                            isChecked ? 'bg-blue-900/40 border border-blue-600/40' : 'hover:bg-blue-900/20 border border-transparent'
                          }`}
                          data-testid={`fab-add-rate-${p.process_code}`}>
                          <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                            isChecked ? 'bg-blue-600 border-blue-500' : 'border-slate-600 bg-slate-800'
                          }`}>
                            {isChecked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[11px] text-slate-200 truncate">{p.process_name}</div>
                            <div className="text-[9px] text-slate-500">{p.process_code}</div>
                          </div>
                          <span className="text-[10px] text-emerald-400/70 font-mono shrink-0 ml-2">{costDisplay(p)}</span>
                        </button>
                      );
                    })}
                  </div>
                ))
              )
            )}
          </div>

          {/* Selection Summary */}
          {selected.size > 0 && (
            <div className="mt-2 border border-blue-600/30 rounded-md bg-blue-950/30 p-2" data-testid="fab-selection-summary">
              <div className="text-[9px] text-blue-400 font-semibold uppercase tracking-wider mb-1">
                Selected ({selected.size})
              </div>
              <div className="space-y-0.5 max-h-[80px] overflow-y-auto">
                {[...selected.values()].map(p => (
                  <div key={p.process_code} className="flex items-center justify-between text-[10px]">
                    <span className="text-slate-300 truncate">{p.process_name}</span>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <span className="text-emerald-400/70 font-mono">{costDisplay(p)}</span>
                      <button onClick={(e) => { e.stopPropagation(); toggleSelect(p); }}
                        className="text-slate-600 hover:text-red-400 transition-colors" data-testid={`fab-deselect-${p.process_code}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Apply Button */}
          {selected.size > 0 && (
            <Button size="sm" onClick={applySelected} disabled={applying}
              className="w-full mt-2 h-7 text-[10px] bg-blue-600 hover:bg-blue-500 text-white font-semibold"
              data-testid="fab-apply-selected-btn">
              {applying ? 'Applying...' : `Apply ${selected.size} Operation${selected.size !== 1 ? 's' : ''}`}
            </Button>
          )}

          <button onClick={() => setShowManual(true)}
            className="w-full mt-2 py-1.5 text-[10px] text-blue-400 hover:text-blue-300 border border-dashed border-blue-600/30 rounded hover:bg-blue-900/20 transition-colors"
            data-testid="fab-manual-entry-btn">
            + Manual Entry (custom operation)
          </button>
        </>
      ) : (
        <div className="space-y-2" data-testid="fab-manual-form">
          <Input value={manualForm.operation_name} onChange={e => setManualForm(f => ({ ...f, operation_name: e.target.value }))}
            placeholder="Operation name *" className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white" />
          <div className="flex gap-2">
            <select value={manualForm.cost_model} onChange={e => setManualForm(f => ({ ...f, cost_model: e.target.value }))}
              className="h-7 text-[10px] bg-slate-800 border border-slate-600 text-white rounded px-2 flex-1"
              data-testid="fab-manual-cost-model">
              <option value="per_piece">Per piece</option>
              <option value="per_unit">Per unit</option>
              <option value="hourly_rate">Hourly rate</option>
              <option value="manual">Manual (flat)</option>
            </select>
            {manualForm.cost_model !== 'manual' && (
              <Input type="number" value={manualForm.rate} onChange={e => setManualForm(f => ({ ...f, rate: e.target.value }))}
                placeholder="Rate" className="h-7 w-24 text-[10px] bg-slate-800 border-slate-600 text-white font-mono" min="0" />
            )}
          </div>
          {manualForm.cost_model === 'per_unit' && (
            <Input type="number" value={manualForm.quantity} onChange={e => setManualForm(f => ({ ...f, quantity: e.target.value }))}
              placeholder="Quantity (e.g. 3 bends)" className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white font-mono" min="1" />
          )}
          {manualForm.cost_model === 'hourly_rate' && (
            <Input type="number" value={manualForm.hours} onChange={e => setManualForm(f => ({ ...f, hours: e.target.value }))}
              placeholder="Hours (e.g. 0.5)" className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white font-mono" min="0.01" step="0.25" />
          )}
          {manualForm.cost_model === 'manual' && (
            <Input type="number" value={manualForm.manual_cost} onChange={e => setManualForm(f => ({ ...f, manual_cost: e.target.value }))}
              placeholder="Total cost" className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white font-mono" min="0" />
          )}
          <Input value={manualForm.notes} onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Notes (optional)" className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white" />
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 h-6 text-[10px] bg-blue-600 hover:bg-blue-500 text-white" onClick={addManual} disabled={adding === 'manual'}>
              {adding === 'manual' ? 'Adding...' : 'Add Operation'}
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300" onClick={() => setShowManual(false)}>Back</Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main Fabrication Operations Section                                 */
/* ------------------------------------------------------------------ */
export const FabricationOpsSection = ({ line, bomId, revisionId, onRefresh, isDraft = true, onBusy }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const ops = line?.fabrication_operations || [];
  const fabTotal = line?.fabrication_cost_total || 0;

  if (!line || line.make_buy !== 'Make' || line.is_assembly) return null;

  return (
    <div className="border border-orange-700/30 rounded-lg p-3 bg-orange-900/10" data-testid="fab-ops-section">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="text-[10px] text-orange-400 font-semibold uppercase tracking-wider flex items-center gap-1">
          <Wrench className="w-3 h-3" /> Fabrication Operations <span className="text-amber-600/60">[8.5.1]</span>
          {ops.length > 0 && <span className="text-orange-300/60 ml-1">({ops.length})</span>}
        </div>
        <div className="flex items-center gap-2">
          {fabTotal > 0 && <span className="font-mono text-emerald-400 text-[10px] font-semibold">{fabTotal.toLocaleString()}</span>}
          {collapsed ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronUp className="w-3 h-3 text-slate-500" />}
        </div>
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-1.5">
          {ops.length === 0 ? (
            <div className="text-[10px] text-slate-500 py-2 text-center border border-dashed border-slate-700/40 rounded">
              No fabrication operations. Add steps this part goes through.
            </div>
          ) : (
            <>
              {ops.map((op, idx) => (
                <OperationRow key={`${op.operation_code}-${idx}`} op={op} idx={idx}
                  bomId={bomId} revisionId={revisionId} lineId={line.line_id}
                  onRefresh={onRefresh} isDraft={isDraft} />
              ))}
              <div className="border-t border-orange-600/20 mt-2 pt-1.5 flex justify-between items-center">
                <span className="text-[10px] text-orange-300 font-semibold">Fabrication Cost:</span>
                <span className="font-mono text-orange-300 text-[11px] font-bold" data-testid="fab-ops-total">{fabTotal.toLocaleString()}</span>
              </div>
            </>
          )}

          {isDraft && !showAdd && (
            <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}
              className="w-full h-6 text-[10px] border-orange-600/30 text-orange-400 hover:bg-orange-900/20 hover:text-orange-300 mt-1"
              data-testid="fab-add-op-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Operation
            </Button>
          )}

          {showAdd && (
            <AddOperationPopover bomId={bomId} revisionId={revisionId} lineId={line.line_id}
              onRefresh={onRefresh} onClose={() => setShowAdd(false)} onBusy={onBusy} />
          )}
        </div>
      )}
    </div>
  );
};
